"use client"

import { useEffect, useRef } from "react"
import { toast } from "sonner"
import { attachShopifyStoreAction } from "@/server-actions/shopify/attach-store"
import { SHOPIFY_ATTACH_PARAM } from "@/lib/shopify/auth/attach-link"
import { useTranslations } from "next-intl"



/**
 * One-shot handler for App-Store-install URL signals landing on the dashboard.
 *
 * - `?shopify_attach=1`: finishes the "add this store to my existing account" branch of
 *   the fork page (`/auth/shopify-connect`); once the user is inside the authenticated
 *   SPA shell (login form first if needed — the param survives it), the attach action
 *   fires exactly once. The pending install itself is identified by the signed
 *   `shopify_claim` cookie, not by anything in the URL.
 * - `?shopify_error=...`: failure message from the install route's top-level redirect —
 *   surfaced as a toast (nothing else consumes it).
 *
 * Mounted only in the authenticated Dashboard, so the action always runs with a session.
 */
export function ShopifyAttachHandler() {

  const tUi = useTranslations("Integrations.IntegrationsShopifyAttachHandler")

  const startedRef = useRef(false)

  useEffect(() => {
    const url = new URL(window.location.href)
    const installError = url.searchParams.get("shopify_error")
    if (installError) {
      url.searchParams.delete("shopify_error")
      window.history.replaceState({}, "", url.toString())
      // Blocking failure — stays until dismissed (closeButton on the Toaster).
      toast.error(installError, { duration: Infinity })
    }
  }, [])

  useEffect(() => {
    const url = new URL(window.location.href)
    if (url.searchParams.get(SHOPIFY_ATTACH_PARAM) !== "1") return
    if (startedRef.current) return
    startedRef.current = true

    // Consume the param up front so page switches / reloads can't re-trigger the attach.
    url.searchParams.delete(SHOPIFY_ATTACH_PARAM)
    window.history.replaceState({}, "", url.toString())

    void (async () => {
      const toastId = toast.loading(tUi("connectingYourShopifyStore"))
      try {
        const result = await attachShopifyStoreAction()
        if (result.ok) {
          toast.success(tUi("value0IsNowConnectedToYourAccount", { value0: String(result.shopDomain) }), {
            id: toastId,
            duration: 6000,
          })
          // Hard navigation so integration-dependent UI (sidebar, gates) re-fetches.
          setTimeout(() => {
            window.location.href = "/?page=integrations"
          }, 1200)
        } else {
          // Blocking failure — stays until dismissed.
          toast.error(result.error, { id: toastId, duration: Infinity })
        }
      } catch {
        toast.error(tUi("couldNotConnectTheStorePleaseTryAgain"), { id: toastId, duration: Infinity })
      }
    })()
  }, [tUi])

  return null
}
